import {Button, CircularProgress} from '@mui/material';
import {DateTime} from 'luxon';
import {useOptimisticSearchParams} from 'nuqs/adapters/react-router/v7';
import {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router';
import {
  SearchQuery,
  useGetCatalogSearch,
  useGetCollection,
} from '@src/api/catalog';
import {notifyError} from '@src/components/notifier/notify';
import {WORLD_EXTENT} from '@src/constants';
import {ANALYZE_ROUTE_KEYS} from '@src/constants/routes';
import useVisualizationParams from '@src/hooks/useVisualizationParams';
import dimensions from '@src/styles/dimensions';
import {Collection} from '@src/types/configurations';
import {parseDateTimeFromCollectionWithDefaults} from '@src/utils/collections';

const getLastMonthTimeRangeFromCollection = (
  collection: Collection,
): string => {
  const {dateTimeTo} = parseDateTimeFromCollectionWithDefaults(collection);
  return `${dateTimeTo.minus({months: 1}).toISO()}/${dateTimeTo.toISO()}`;
};

const parseExtentBboxFromCollection = (collection: Collection) => {
  return collection?.extent?.spatial?.bbox?.[0] ?? WORLD_EXTENT;
};

export const VisualizeButton = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {collectionId, configurationId} = useVisualizationParams();
  const searchParams = useOptimisticSearchParams();
  const {data: collection} = useGetCollection(collectionId);

  const [searchQueryParams, setSearchQueryParams] = useState<
    SearchQuery | undefined
  >();

  const {data: searchResults, isLoading: isLoadingResults} =
    useGetCatalogSearch(searchQueryParams!, {enabled: !!searchQueryParams});

  const {bbox: firstResultBBox, properties} =
    searchResults?.features?.[0] || {};
  const firstResultDate = properties?.datetime;
  if (firstResultDate) {
    const dateTimeFrom = DateTime.fromISO(firstResultDate)
      .startOf('day')
      .toISO();
    const dateTimeTo = DateTime.fromISO(firstResultDate).endOf('day').toISO();
    searchParams.set('from', dateTimeFrom ?? '');
    searchParams.set('to', dateTimeTo ?? '');
  }

  useEffect(() => {
    if (searchResults && firstResultBBox?.length === 4) {
      try {
        const navigateParams = new URLSearchParams({
          ...Object.fromEntries(searchParams.entries()),
        });
        navigate(
          {
            pathname: `/analyze/${ANALYZE_ROUTE_KEYS.VISUALIZE}`,
            search: navigateParams.toString(),
          },
          {state: {mapToFitBBox: firstResultBBox}},
        );
      } catch (error) {
        notifyError({
          data: {
            message: 'Error fetching catalog data',
          },
        });
      } finally {
        setSearchQueryParams(undefined);
      }
    }
  }, [firstResultBBox, dispatch, navigate, searchResults, searchParams]);

  const isDisabled = !configurationId || !collectionId || isLoadingResults;
  return (
    <Button
      onClick={() => {
        if (!collection) {
          return;
        }
        setSearchQueryParams({
          bbox: parseExtentBboxFromCollection(collection),
          datetime: getLastMonthTimeRangeFromCollection(collection),
          collections: [collectionId],
          limit: 10,
        });
      }}
      disabled={isDisabled}
      variant="contained"
      color="primary"
      size="large"
      sx={{
        marginBlockStart: 0.25,
        marginBlockEnd: 1,
        display: 'flex',
        justifyContent: 'center',
        marginInline: `${dimensions.leftSidebar.contentMargin}px`,
      }}
    >
      {isLoadingResults ? (
        <CircularProgress size={24} sx={{marginBlock: 0.15}} />
      ) : (
        'Visualize'
      )}
    </Button>
  );
};

export default VisualizeButton;
